'use es6';
/*
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { runOnce } from './runOnce.js';
/**
 * Runs the passed callback during the next idle period, or immediately
 * if the browser's visibility state is (or becomes) hidden.
 */
export const whenIdleOrHidden = (cb) => {
    // Cap the requestIdleCallback to 1 sec for very busy apps
    // https://github.com/GoogleChrome/web-vitals/issues/754
    // If not using rIC, then the setTimeout timeout should be 0
    const timeout = 'requestIdleCallback' in globalThis ? 1000 : 0;
    const rIC = globalThis.requestIdleCallback || setTimeout;
    const cIC = globalThis.cancelIdleCallback || clearTimeout;
    // If the document is hidden, run the callback immediately, otherwise
    // race an idle callback with the next `visibilitychange` event.
    if (document.visibilityState === 'hidden') {
        cb();
    }
    else {
        const wrappedCb = runOnce(cb);
        let idleHandle = -1;
        const onHidden = () => {
            cIC(idleHandle);
            wrappedCb();
        };
        addEventListener('visibilitychange', onHidden, { once: true, capture: true });
        idleHandle = rIC(() => {
            removeEventListener('visibilitychange', onHidden, { capture: true });
            wrappedCb();
        }, { timeout: timeout });
    }
};
//# sourceMappingURL=whenIdleOrHidden.js.map